import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormIcon, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { userPermissions } from "@/constants/user-permissions";
import { userRoles } from "@/constants/user-roles";
import { type LoginFormSchema, loginFormSchema } from "@/features/dashboard/types/user";
import { useAuth } from "@/hooks/use-auth.tsx";
import { useValidationError } from "@/hooks/use-validation-errors";
import { sleep } from "@/utils/sleep.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconAlertHexagon, IconLock, IconUser } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

export const Route = createFileRoute("/login")({
	component: LoginPage,
});

function LoginPage() {
	const { user } = useAuth();
	console.log(user);

	useEffect(() => {
		if (user?.id) {
			navigate({ to: "/bcc-dashboard" });
		}
	}, []);
	const devMode = import.meta.env.DEV;
	const { login, resendVerification } = useAuth();
	const navigate = useNavigate({ from: "/login" });
	const [errors, setErrors, clearError, getFirstError] = useValidationError();
	const [resendSuccess, setResendSuccess] = useState<string | null>(null);

	const hasRole = (roles: string[], user) => {
		return roles.some((role) => {
			const userRoles = user?.roles.flatMap((r) => r.name) || [];

			return userRoles.includes(role);
		});
	};

	const hasPermission = (permissions: string[], user) => {
		return permissions.some((permission) => {
			const userPermissions = user?.permissions.flatMap((r) => r.name) || [];

			return userPermissions.includes(permission);
		});
	};

	const handleSubmit = async (data: LoginFormSchema) => {
		clearError();

		try {
			const user = await login(data);
			sessionStorage.setItem("password_entered_at", null);
			await sleep(1000);

			if (hasRole([userRoles.SUPER_ADMIN], user)) {
				await navigate({ to: "/" });
			} else if (hasRole([userRoles.BCC, "BCC Staff"], user) || hasPermission([userPermissions.BCC], user)) {
				await navigate({ to: "/bcc-dashboard" });
			} else if (hasRole([userRoles.OCC], user)) {
				await navigate({ to: "/occ-dashboard" });
			} else if (hasRole([userRoles.DASHBOARD], user)) {
				await navigate({ to: "/statistics" });
			} else if (hasRole([userRoles.JUDGE], user)) {
				await navigate({ to: "/judge-dashboard" });
			} else {
				await navigate({ to: "/bcc-dashboard" });
			}
		} catch (e) {
			form.setValue("password", "");
			if (e instanceof AxiosError) {
				const status = e.response?.status;
				const data = e.response?.data ?? {};

				if (data?.errors) {
					setErrors(data.errors);
				} else if (status === 429 && data?.message) {
					setErrors({ _general: [data.message] });
				} else {
					setErrors({ _general: ["Login failed. Please try again."] });
				}
			}
		}
	};

	const handleResendVerification = async (email: string) => {
		try {
			const response = await resendVerification({ email });

			setResendSuccess(response.message || "If your email exists in our system, a verification link has been sent.");

			clearError();
		} catch (error) {
			console.error(error);
		}
	};

	const form = useForm({
		resolver: zodResolver(loginFormSchema),
		defaultValues: {
			email: devMode ? "superuser@example.com" : "",
			password: devMode ? "password" : "",
			remember: true,
		},
		mode: "onChange",
	});

	return (
		<div className="flex flex-row w-full h-[100svh] p-4 bg-background">
			<div className="relative flex flex-col w-1/2 h-full rounded-lg bg-slate-100 overflow-hidden">
				<div className="absolute inset-0 flex flex-col justify-center items-center w-full h-full p-8">
					<div className="relative flex flex-col justify-center items-center w-full max-w-[420px] gap-6">
						<div className="z-[2] flex flex-col justify-center items-center w-full gap-2">
							<h1 className="text-3xl font-bold text-center text-gray-800">Case Management System</h1>
							<p className="text-lg text-foreground text-center text-gray-700">
								Secure access to the Case Management System. Please log in to ensure data integrity and confidentiality.
							</p>
						</div>
						<div className="z-[2] inline-flex py-2 px-3 rounded-md bg-primary-600">
							<span className=" font-semibold text-sm text-white">Version {import.meta.env.VITE_APP_VERSION}</span>
						</div>
						<div className="z-[1] absolute inset-0 left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-primary-100 blur-2xl opacity-50" />
					</div>
				</div>
			</div>

			<div className="flex flex-col justify-center items-center w-1/2 p-6">
				<div className="flex w-full max-w-[420px]">
					<Form {...form}>
						<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
							{import.meta.env.DEV && !errors.length && (
								<Alert type="warning" allowClose={true} title="You are in development mode!" icon={<IconAlertCircle size={20} stroke={2} />} className="mb-2">
									If you are seeing this message, you are in development mode. Do not store any sensitive information.
								</Alert>
							)}

							{resendSuccess && (
								<Alert type="success" title="Success" className="mb-2">
									{resendSuccess}
								</Alert>
							)}

							{!!errors.length && (
								<Alert type="error" title="Login Failed" className="mb-2" icon={<IconAlertHexagon size={16} stroke={2} />}>
									{getFirstError() === "account_not_verified" ? (
										<>
											Your account has not been verified.{" "}
											<button type="button" onClick={() => handleResendVerification(form.getValues().email)} className="underline text-primary-600">
												Click here to resend the verification email.
											</button>
										</>
									) : (
										getFirstError()
									)}
								</Alert>
							)}

							<div className="grid grid-cols-12 w-full gap-3">
								<div className="col-span-12 flex flex-col w-full">
									<FormField
										control={form.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Email</FormLabel>
												</div>
												<FormGroup iconPosition="left">
													<FormIcon>
														<IconUser size={18} stroke={1.5} />
													</FormIcon>
													<Input autoComplete="off" placeholder="Email" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-12 flex flex-col w-full">
									<FormField
										control={form.control}
										name="password"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Password</FormLabel>
												</div>
												<FormGroup iconPosition="left">
													<Input type="password" placeholder="Password" {...field} />
													<FormIcon>
														<IconLock size={18} stroke={1.5} />
													</FormIcon>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-12 flex flex-col w-full mt-1 gap-2">
									<Button loading={form.formState.isSubmitting} type="submit" className="w-full">
										Sign-in
									</Button>
									{/*<Link to="/forgot-password">
										<Button className="w-full" variant="ghost">
											Forgot Password
										</Button>
									</Link>*/}
								</div>
							</div>
							{/*<div className="flex flex-row items-center justify-center w-full my-6">
								<hr className="w-full h-px border-t border-border" />
								<div className="flex-shrink-0 whitespace-nowrap text-sm uppercase text-muted-foreground text-center px-3">
									or Sign-in with
								</div>
								<hr className="w-full h-px border-t border-border" />
							</div>
							<div className="grid grid-cols-12 w-full gap-2">
								<div className="col-span-12 flex w-full">
									<Button className="w-full" variant="ghost">
										Sign-in with Passkey
									</Button>
								</div>
							</div>*/}
						</form>
					</Form>
				</div>
			</div>
		</div>
	);
}
